import * as React from 'react';

import { Convert } from 'core/utils';
import type { LoadedImage, StyleButtonMask } from './StyleButtonImagePickerTypes';
import { Res } from 'core/api';

export type StyleButtonImageEditorHandle = {
	exportImage: () => Promise<string>;
};

type EditorTransform = {
	offsetX: number;
	offsetY: number;
	zoom: number;
	rotation: number;
};

const MIN_ZOOM = 0;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.05;
const MIN_ROTATION = -180;
const MAX_ROTATION = 180;
const CROP_MASK_INSET = 24; // Debe coincidir con .style-button-picker-mask { inset:  } del fichero less
const RESET_TRANSFORM: EditorTransform = { offsetX: 0, offsetY: 0, zoom: 1, rotation: 0 };

const degreesToRadians = (degrees: number) => degrees * Math.PI / 180;

const getRotatedSize = (width: number, height: number, rotation: number) => {
	const radians = degreesToRadians(rotation);
	const cos = Math.abs(Math.cos(radians));
	const sin = Math.abs(Math.sin(radians));

	return {
		width: width * cos + height * sin,
		height: width * sin + height * cos,
	};
};

const getBaseScale = (image: LoadedImage, viewport: { width: number; height: number }) =>
	Math.min(viewport.width / image.naturalWidth, viewport.height / image.naturalHeight);

function getPreviewMaskBorderRadius(mask: StyleButtonMask, viewport: { width: number; height: number }) {
	const shortestMaskSide = Math.min(mask.width, mask.height);
	const shortestViewportSide = Math.min(viewport.width, viewport.height);
	const isCircle = mask.borderRadiusPx >= (shortestMaskSide / 2);
	if (isCircle)
		return shortestViewportSide / 2;

	return mask.borderRadiusPx * (viewport.width / mask.width);
}

function clampEditorTransform(
	image: LoadedImage,
	transform: EditorTransform,
	viewport: { width: number; height: number }
): EditorTransform {
	const baseScale = getBaseScale(image, viewport);
	const totalScale = baseScale * transform.zoom;
	const rotated = getRotatedSize(image.naturalWidth * totalScale, image.naturalHeight * totalScale, transform.rotation);

	const maxOffsetX = Math.max(0, (rotated.width - viewport.width) / 2);
	const maxOffsetY = Math.max(0, (rotated.height - viewport.height) / 2);

	return {
		...transform,
		offsetX: Math.min(maxOffsetX, Math.max(-maxOffsetX, transform.offsetX)),
		offsetY: Math.min(maxOffsetY, Math.max(-maxOffsetY, transform.offsetY)),
	};
}

async function exportStyleButtonImage(params: {
	image: LoadedImage;
	transform: EditorTransform;
	mask: StyleButtonMask;
	viewport: { width: number; height: number };
}) {
	const { image, transform, mask, viewport } = params;
	const canvas = document.createElement('canvas');
	canvas.width = mask.width;
	canvas.height = mask.height;

	const ctx = canvas.getContext('2d');
	if (!ctx)
		throw new Error('No se pudo crear el contexto de canvas');

	const scaleRatio = mask.width / viewport.width;
	const baseScale = getBaseScale(image, viewport);
	const totalScale = baseScale * transform.zoom * scaleRatio;

	ctx.save();
	ctx.translate((canvas.width / 2) + (transform.offsetX * scaleRatio), (canvas.height / 2) + (transform.offsetY * scaleRatio));
	ctx.rotate(degreesToRadians(transform.rotation));
	ctx.scale(totalScale, totalScale);
	ctx.drawImage(
		await Convert.imageSrcToHTMLImage(image.src),
		-image.naturalWidth / 2,
		-image.naturalHeight / 2
	);
	ctx.restore();

	const dataUrl = canvas.toDataURL('image/png');
	return dataUrl.split(',')[1] || '';
}

type Props = {
	backgroundColor?: string;
	image: LoadedImage;
	mask: StyleButtonMask;
	onChangeImage: () => void;
};

export const StyleButtonImageEditor = React.forwardRef<StyleButtonImageEditorHandle, Props>(function StyleButtonImageEditor(props, ref) {
	const [transform, setTransform] = React.useState<EditorTransform>(RESET_TRANSFORM);
	const [viewport, setViewport] = React.useState(() => {
		const aspect = props.mask.width / props.mask.height;
		const width = props.mask.previewWidth;
		return { width, height: Math.round(width / aspect) };
	});

	const previewFrameRef = React.useRef<HTMLDivElement>(null);
	const dragRef = React.useRef<{ x: number; y: number; transform: EditorTransform } | null>(null);
	const cropViewport = React.useMemo(() => ({
		width: Math.max(1, viewport.width - (CROP_MASK_INSET * 2)),
		height: Math.max(1, viewport.height - (CROP_MASK_INSET * 2)),
	}), [viewport.height, viewport.width]);

	const updateTransform = React.useCallback((nextTransform: EditorTransform | ((prev: EditorTransform) => EditorTransform)) => {
		setTransform(previous => {
			const resolved = typeof nextTransform === 'function' ? nextTransform(previous) : nextTransform;
			return clampEditorTransform(props.image, resolved, cropViewport);
		});
	}, [cropViewport, props.image]);

	const onMouseDown = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
		event.preventDefault();
		dragRef.current = {
			x: event.clientX,
			y: event.clientY,
			transform,
		};
	}, [transform]);

	const previewMaskBorderRadius = React.useMemo(
		() => getPreviewMaskBorderRadius(props.mask, cropViewport),
		[cropViewport, props.mask]
	);

	const imageStyle = React.useMemo<React.CSSProperties>(() => {
		const baseScale = getBaseScale(props.image, cropViewport);
		const totalScale = baseScale * transform.zoom;

		return {
			position: 'absolute',
			left: '50%',
			top: '50%',
			width: `${props.image.naturalWidth}px`,
			height: `${props.image.naturalHeight}px`,
			transform: `translate(-50%, -50%) translate(${transform.offsetX}px, ${transform.offsetY}px) scale(${totalScale}) rotate(${transform.rotation}deg)`,
			transformOrigin: 'center center',
			userSelect: 'none',
			pointerEvents: 'none',
			maxWidth: 'none',
		};
	}, [cropViewport, props.image, transform]);

	React.useEffect(() => {
		setTransform(RESET_TRANSFORM);
	}, [props.image]);

	React.useEffect(() => {
		if (!previewFrameRef.current)
			return;

		const updateViewport = () => {
			const containerWidth = previewFrameRef.current?.clientWidth || props.mask.previewWidth;
			const nextWidth = Math.min(props.mask.previewWidth, containerWidth);
			if (!nextWidth)
				return;

			const nextHeight = Math.round(nextWidth * (props.mask.height / props.mask.width));
			setViewport(previous =>
				previous.width === nextWidth && previous.height === nextHeight
					? previous
					: {
						width: nextWidth,
						height: nextHeight,
					});
		};

		updateViewport();

		if (typeof ResizeObserver === 'undefined')
			return;

		const observer = new ResizeObserver(updateViewport);
		observer.observe(previewFrameRef.current);
		return () => observer.disconnect();
	}, [props.mask.height, props.mask.previewWidth, props.mask.width]);

	React.useEffect(() => {
		setTransform(previous => clampEditorTransform(props.image, previous, cropViewport));
	}, [cropViewport, props.image]);

	React.useEffect(() => {
		const onMouseMove = (event: MouseEvent) => {
			const dragState = dragRef.current;
			if (!dragState)
				return;

			updateTransform({
				...dragState.transform,
				offsetX: dragState.transform.offsetX + (event.clientX - dragState.x),
				offsetY: dragState.transform.offsetY + (event.clientY - dragState.y),
			});
		};

		const onMouseUp = () => {
			dragRef.current = null;
		};

		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', onMouseUp);
		return () => {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', onMouseUp);
		};
	}, [updateTransform]);

	React.useImperativeHandle(ref, () => ({
		exportImage: async () => await exportStyleButtonImage({
			image: props.image,
			transform,
			mask: props.mask,
			viewport: cropViewport,
		}),
	}), [cropViewport, props.image, props.mask, transform]);

	return <div className='style-button-picker-editor-step'>
		<div className='style-button-picker-editor-toolbar'>
			<div className='style-button-picker-help'>{Res.WA.WA_STYLE_BUTTON_MOVE_IMAGE_HELP_TEXT()}</div>
			<a className='link font-bold' onClick={props.onChangeImage}><i className='fa fa-refresh m-r-sm'></i>{Res.WA.WA_STYLE_BUTTON_CHANGE_IMAGE_BTN()}</a>
		</div>

		<div ref={previewFrameRef}>
			<div
				onMouseDown={onMouseDown}
				className='style-button-picker-preview'
				style={{
					maxWidth: viewport.width,
					height: viewport.height,
					cursor: 'grab'
				}}
			>
				{props.backgroundColor &&
					<div style={{ position: 'absolute', inset: 0, backgroundColor: props.backgroundColor }} />}

				<img src={props.image.src} alt='Preview' style={imageStyle} />
				<div className='style-button-picker-mask' style={{ borderRadius: previewMaskBorderRadius }} />
			</div>
		</div>

		<div className='m-t-md'>
			<div className='form-group'>
				<label className='control-label'>{Res.WA.WA_STYLE_BUTTON_ZOOM_LBL()}</label>
				<input className='style-button-picker-range' type='range' min={MIN_ZOOM} max={MAX_ZOOM} step={ZOOM_STEP} value={transform.zoom}
					onChange={event => updateTransform(previous => ({ ...previous, zoom: Number(event.target.value) }))} />
			</div>
			<div className='form-group'>
				<label className='control-label'>{Res.WA.WA_STYLE_BUTTON_ROTATE_LBL()}</label>
				<input className='style-button-picker-range' type='range' min={MIN_ROTATION} max={MAX_ROTATION} step={1} value={transform.rotation}
					onChange={event => updateTransform(previous => ({ ...previous, rotation: Number(event.target.value) }))} />
			</div>
		</div>
	</div>;
});
